import React, { useState, useEffect, useRef } from "react";
import { X, AlertTriangle, ExternalLink } from "lucide-react";
import HijriDatePicker from "@/components/HijriDatePicker";
import AttachmentsPanel from "@/components/AttachmentsPanel";
import api from "@/lib/api";
import { useT } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";

export default function EmployeeForm({ employee, branches, admins, onClose, onSaved, emFields }) {
  const { t } = useT();
  const { user } = useAuth();
  const scrollRef = useRef(null);
  const [em, setEm] = useState({
    company: emFields?.company || "",
    nationality: emFields?.nationality || "",
    old_id: emFields?.old_id || "",
    em_note: emFields?.em_note || "",
    branch_city: emFields?.branch_city || "",
    branch_type: emFields?.branch_type || "",
  });
  const [form, setForm] = useState({
    employee_id: "",
    name: "",
    position: "",
    iqama_number: "",
    phone: "",
    hire_date: "",
    branch_id: "",
    admin_id: "",
    health_expiry_hijri: null,
    training_expiry_hijri: null,
    municipality_card: false,
    printed_photo: false,
    notes: "",
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!employee) return;
    // Prefill for either existing employee OR a "new" one with preset fields
    // (e.g. the Add-My-Cert button preloads name / branch_id / position).
    setForm({
      employee_id: employee.employee_id || "",
      name: employee.name || "",
      position: employee.position || "",
      iqama_number: employee.iqama_number || "",
      phone: employee.phone || "",
      hire_date: employee.hire_date || "",
      branch_id: employee.branch_id || "",
      admin_id: employee.admin_id || "",
      health_expiry_hijri: employee.health_expiry_hijri || null,
      training_expiry_hijri: employee.training_expiry_hijri || null,
      municipality_card: !!employee.municipality_card,
      printed_photo: !!employee.printed_photo,
      notes: employee.notes || "",
    });
    // Scroll modal content to top whenever the modal opens or employee changes
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [employee]);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Auto-set admin when branch changes (based on branch.admin_id)
  // - Vacation branch: no admin
  // - Admin branch: keep admin_id selectable (AM's own cert vs supervisor's cert). Auto-fill position hint only.
  const onBranchChange = (newBranchId) => {
    setForm((f) => {
      const next = { ...f, branch_id: newBranchId };
      const branch = branches.find((b) => b.id === newBranchId);
      if (branch && branch.is_vacation) {
        next.admin_id = "";
      } else if (branch && branch.is_admin) {
        // Preserve admin_id only if it belongs to this branch's area manager.
        const keep = next.admin_id && admins.some((a) => a.id === next.admin_id && (!a.area_manager_id || a.area_manager_id === branch.area_manager_id));
        if (!keep) next.admin_id = "";
        next.position = next.admin_id ? "Supervisor" : "Area Manager";
      } else if (branch && branch.admin_id) {
        next.admin_id = branch.admin_id;
      }
      return next;
    });
  };

  const currentBranch = branches.find((b) => b.id === form.branch_id);
  const isOnVacation = !!currentBranch?.is_vacation;
  const isOnAdmin = !!currentBranch?.is_admin;
  const regularBranches = branches.filter((b) => !b.is_vacation && !b.is_admin);
  const vacationBranch = branches.find((b) => b.is_vacation);
  const adminBranches = branches.filter((b) => b.is_admin);
  // Supervisors limited to the selected branch's area manager (super admin sees all AMs' branches)
  const scopedAdmins = currentBranch?.area_manager_id
    ? admins.filter((a) => !a.area_manager_id || a.area_manager_id === currentBranch.area_manager_id)
    : admins;

  // For admin-branch entries the "supervisor" shown is the Area Manager owning that admin branch.
  const [amDirectory, setAmDirectory] = useState({});
  const [positionsCatalog, setPositionsCatalog] = useState([]);
  useEffect(() => {
    // Only fetch if role can see other AMs
    if (user?.role === "super_admin" || user?.role === "area_manager" || user?.role === "supervisor") {
      api.get("/area-managers/peers")
        .then((r) => setAmDirectory(Object.fromEntries((r.data || []).map((am) => [am.id, am.full_name || am.username]))))
        .catch(() => {});
    }
    api.get("/positions").then((r) => setPositionsCatalog(r.data || [])).catch(() => setPositionsCatalog([]));
  }, [user?.role]);
  const adminBranchAreaManagerName = currentBranch?.is_admin
    ? (amDirectory[currentBranch.area_manager_id] || (user?.id === currentBranch.area_manager_id ? user?.full_name : ""))
    : "";

  // Positions filtered based on the currently selected branch's scope
  const positionOptions = React.useMemo(() => {
    const list = positionsCatalog.length ? positionsCatalog : [
      { id: "d1", name: "Barista", scope: "branch" },
      { id: "d2", name: "Shift Leader", scope: "branch" },
      { id: "d3", name: "Supervisor", scope: "admin" },
      { id: "d4", name: "Area Manager", scope: "admin" },
    ];
    if (isOnAdmin) return list.filter((p) => p.scope === "admin" || p.scope === "all");
    return list.filter((p) => p.scope === "branch" || p.scope === "all");
  }, [positionsCatalog, isOnAdmin]);

  const POSITIONS = positionOptions.map((p) => ({ value: p.name, label: p.name }));
  const positionValues = POSITIONS.map((p) => p.value);
  const isCustomPosition = !!form.position && !positionValues.includes(form.position);

  // Live duplicate detection while user types iqama / employee_id / phone
  const [dupMatches, setDupMatches] = useState([]);
  const [dupBlocking, setDupBlocking] = useState([]); // shown as blocking modal on submit
  useEffect(() => {
    const iqama = (form.iqama_number || "").trim();
    const empno = (form.employee_id || "").trim();
    const phone = (form.phone || "").trim();
    if (!iqama && !empno && !phone) { setDupMatches([]); return; }
    const handle = setTimeout(async () => {
      try {
        const res = await api.post("/employees/check-duplicate", {
          iqama_number: iqama || null,
          employee_id: empno || null,
          phone: phone || null,
          exclude_id: employee?.id || null,
        });
        setDupMatches(res.data.duplicates || []);
      } catch { setDupMatches([]); }
    }, 400);
    return () => clearTimeout(handle);
  }, [form.iqama_number, form.employee_id, form.phone, employee?.id]);

  const submit = async () => {
    setErr("");
    if (!form.name.trim()) {
      setErr(t("name_required"));
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.branch_id) payload.branch_id = null;
      if (!payload.admin_id) payload.admin_id = null;
      if (employee?.id) await api.put(`/employees/${employee.id}`, payload);
      else {
        const res = await api.post("/employees", payload);
        if (emFields && res?.data?.id) {
          try { await api.patch(`/em/employees/${res.data.id}`, em); } catch { /* main save succeeded */ }
        }
      }
      if (emFields && employee?.id) {
        try { await api.patch(`/em/employees/${employee.id}`, em); } catch { /* main save succeeded */ }
      }
      onSaved();
    } catch (e) {
      const detail = e.response?.data?.detail;
      // Duplicate response from backend (structured 409)
      if (e.response?.status === 409 && detail && typeof detail === "object" && detail.duplicates) {
        setDupBlocking(detail.duplicates);
      } else {
        setErr(typeof detail === "string" ? detail : t("save_failed"));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 modal-backdrop animate-fade-in" onClick={onClose}>
      <div ref={scrollRef} className="bg-white rounded-2xl card-shadow w-full max-w-3xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between rounded-t-2xl">
          <h3 className="text-lg font-bold text-teal-900">
            {employee?.id ? t("edit_employee") : t("add_new_employee")}
          </h3>
          <button className="btn btn-ghost" onClick={onClose}><X size={18} /></button>
        </div>

        <div className="p-6 space-y-6">
          {err && <div className="status-expired pill px-3 py-2">{err}</div>}

          {/* Basic info */}
          <section>
            <h4 className="text-sm font-bold text-teal-800 mb-3 border-r-4 border-teal-500 pr-2">{t("basic_info")}</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="inp-label">{t("employee_id")}</label>
                <input className="inp" value={form.employee_id} onChange={(e) => upd("employee_id", e.target.value)} data-testid="emp-empid-input" />
              </div>
              <div>
                <label className="inp-label">{t("name")} *</label>
                <input className="inp" value={form.name} onChange={(e) => upd("name", e.target.value)} data-testid="emp-name-input" />
              </div>
              <div>
                <label className="inp-label">{t("position")}</label>
                <select
                  className="inp"
                  value={isCustomPosition ? "__other__" : form.position}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === "__other__") upd("position", "");
                    else upd("position", v);
                  }}
                  data-testid="emp-position-select"
                >
                  <option value="">— —</option>
                  {POSITIONS.map((p) => (
                    <option key={p.value} value={p.value} style={{direction: "ltr"}}>{p.label}</option>
                  ))}
                  <option value="__other__">{t("position_other") + "…"}</option>
                </select>
                {(isCustomPosition || (form.position === "" && false)) && (
                  <input
                    className="inp mt-2"
                    value={form.position}
                    onChange={(e) => upd("position", e.target.value)}
                    placeholder={t("position_custom_placeholder")}
                    data-testid="emp-position-input"
                  />
                )}
              </div>
              <div>
                <label className="inp-label">{t("iqama_number")}</label>
                <input className="inp" value={form.iqama_number} onChange={(e) => upd("iqama_number", e.target.value)} data-testid="emp-iqama-input" />
              </div>
              <div>
                <label className="inp-label">{t("employee_phone")}</label>
                <input className="inp" value={form.phone} onChange={(e) => upd("phone", e.target.value)} placeholder="05XXXXXXXX" style={{direction:"ltr"}} data-testid="emp-phone-input" />
              </div>
              <div>
                <label className="inp-label">{t("emp_hire_date")}</label>
                <input type="date" className="inp" value={form.hire_date} onChange={(e) => upd("hire_date", e.target.value)} style={{direction:"ltr"}} data-testid="emp-hire-date-input" />
                <div className="text-[10px] text-slate-400 mt-1">{t("hire_date_hint")}</div>
              </div>
              <div>
                <label className="inp-label">{t("branch")}</label>
                <select
                  className={`inp ${isOnVacation ? "border-amber-400 bg-amber-50" : (isOnAdmin ? "border-slate-500 bg-slate-50" : "")}`}
                  value={form.branch_id}
                  onChange={(e) => onBranchChange(e.target.value)}
                  data-testid="emp-branch-select"
                >
                  <option value="">{t("select_branch")}</option>
                  {vacationBranch && (
                    <option value={vacationBranch.id}>🏖️ {t("vacation")}</option>
                  )}
                  {adminBranches.map((ab) => (
                    <option key={ab.id} value={ab.id}>
                      ⚙️ {t("admin_branch_label")}{adminBranches.length > 1 && amDirectory[ab.area_manager_id] ? ` — ${amDirectory[ab.area_manager_id]}` : ""}
                    </option>
                  ))}
                  {regularBranches.map((b) => <option key={b.id} value={b.id}>{b.code ? `[${b.code}] ` : ""}{b.name}</option>)}
                </select>
              </div>
              {isOnAdmin && user?.role === "super_admin" && adminBranches.length > 1 && (
                <div>
                  <label className="inp-label">{t("area_manager")}</label>
                  <select
                    className="inp"
                    value={form.branch_id}
                    onChange={(e) => onBranchChange(e.target.value)}
                    data-testid="emp-am-branch-select"
                  >
                    {adminBranches.map((ab) => (
                      <option key={ab.id} value={ab.id}>
                        👑 {amDirectory[ab.area_manager_id] || t("area_manager")}
                      </option>
                    ))}
                  </select>
                  <p className="text-[11px] text-gray-400 mt-1">{t("am_switch_hint")}</p>
                </div>
              )}
              <div>
                <label className="inp-label">{isOnAdmin ? t("cert_owner_label") : t("admin")}</label>
                {isOnAdmin ? (
                  <select
                    className="inp"
                    value={form.admin_id || "__me_am__"}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === "__me_am__") {
                        setForm((f) => ({ ...f, admin_id: "", position: "Area Manager" }));
                      } else {
                        const sup = scopedAdmins.find((a) => a.id === v);
                        setForm((f) => ({
                          ...f,
                          admin_id: v,
                          position: "Supervisor",
                          name: f.name || (sup?.name || ""),
                          phone: f.phone || (sup?.phone || ""),
                          email: f.email || (sup?.email || ""),
                        }));
                      }
                    }}
                    data-testid="emp-admin-owner-select"
                  >
                    <option value="__me_am__">
                      🧑‍💼 {t("cert_owner_am")}{adminBranchAreaManagerName ? ` — ${adminBranchAreaManagerName}` : ""}
                    </option>
                    {scopedAdmins.map((a) => (
                      <option key={a.id} value={a.id}>👔 {a.name} — {t("role_supervisor_short")}</option>
                    ))}
                  </select>
                ) : (
                  <select
                    className="inp"
                    value={form.admin_id}
                    onChange={(e) => upd("admin_id", e.target.value)}
                    disabled={isOnVacation}
                    data-testid="emp-admin-select"
                  >
                    <option value="">{t("select_admin")}</option>
                    {scopedAdmins.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                  </select>
                )}
                {isOnVacation && (
                  <p className="text-xs text-amber-700 mt-1">{t("vacation_no_admin")}</p>
                )}
                {isOnAdmin && (
                  <p className="text-xs text-slate-600 mt-1">{t("cert_owner_help")}</p>
                )}
              </div>
            </div>
          </section>

          {/* Health certificate */}
          {!emFields && (<>
          <section>
            <h4 className="text-sm font-bold text-teal-800 mb-3 border-r-4 border-emerald-500 pr-2">{t("health_cert_section")}</h4>
            <HijriDatePicker
              label={t("expiry_date")}
              value={form.health_expiry_hijri}
              onChange={(v) => upd("health_expiry_hijri", v)}
              testid="emp-health-expiry"
            />
          </section>

          {/* Training */}
          <section>
            <h4 className="text-sm font-bold text-teal-800 mb-3 border-r-4 border-indigo-500 pr-2">{t("training_section")}</h4>
            <HijriDatePicker
              label={t("expiry_date")}
              value={form.training_expiry_hijri}
              onChange={(v) => upd("training_expiry_hijri", v)}
              testid="emp-training-expiry"
            />
          </section>
          </>)}

          {/* Additional info */}
          <section>
            {!emFields && (
              <>
                <h4 className="text-sm font-bold text-teal-800 mb-3 border-r-4 border-amber-500 pr-2">{t("additional_info")}</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <label className="flex items-center gap-2 cursor-pointer bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5">
                    <input type="checkbox" className="h-4 w-4 accent-teal-600" checked={form.municipality_card} onChange={(e) => upd("municipality_card", e.target.checked)} data-testid="emp-muni-card-checkbox" />
                    <span className="text-sm font-medium">{t("municipality_card")}</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5">
                    <input type="checkbox" className="h-4 w-4 accent-teal-600" checked={form.printed_photo} onChange={(e) => upd("printed_photo", e.target.checked)} data-testid="emp-printed-photo-checkbox" />
                    <span className="text-sm font-medium">{t("printed_photo")}</span>
                  </label>
                </div>
              </>
            )}
            <div className="mt-4">
              <label className="inp-label">{t("notes")}</label>
              <textarea className="inp" rows={3} value={form.notes} onChange={(e) => upd("notes", e.target.value)} data-testid="emp-notes-input" />
            </div>

            {emFields && (
              <div className="border-t border-slate-100 pt-3 mt-4">
                <div className="text-xs font-black text-sky-700 mb-2">{t("em_staff_fields_title")}</div>
                <div className="grid grid-cols-2 gap-2">
                  {[["company", "COMPANY"], ["nationality", "NATIONALITY"], ["old_id", "OLD ID"], ["em_note", "NOTE"], ["branch_city", "City"], ["branch_type", "B Type"]].map(([k, label]) => (
                    <div key={k}>
                      <label className="inp-label">{label}</label>
                      <input className="inp" value={em[k]} onChange={(e) => setEm((s) => ({ ...s, [k]: e.target.value }))} data-testid={`emp-em-${k}-input`} />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </section>

          {/* Attachments (municipality) */}
          {!emFields && (
            <section>
              <h4 className="text-sm font-bold text-teal-800 mb-3 border-r-4 border-rose-500 pr-2">📎 {t("attachments")}</h4>
              <AttachmentsPanel
                employeeId={employee?.id}
                attachments={employee?.attachments || []}
                onChange={() => { /* attachments saved server-side immediately */ }}
              />
            </section>
          )}
        </div>

        <div className="sticky bottom-0 bg-white border-t border-gray-100 px-6 py-4 flex justify-end gap-2 rounded-b-2xl">
          <button className="btn btn-secondary" onClick={onClose}>{t("cancel")}</button>
          <button className="btn btn-primary" onClick={submit} disabled={saving} data-testid="save-emp-button">
            {saving ? t("saving") : t("save")}
          </button>
        </div>
      </div>

      {dupBlocking.length > 0 && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50" onClick={() => setDupBlocking([])}>
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="dup-blocking-modal">
            <div className="p-5 border-b border-amber-100 flex items-center gap-2 bg-amber-50 rounded-t-2xl">
              <AlertTriangle size={20} className="text-amber-700" />
              <h3 className="font-bold text-amber-900 text-lg">{t("duplicate_blocked_title")}</h3>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-sm text-gray-700">{t("duplicate_blocked_desc")}</p>
              <div className="space-y-2">
                {dupBlocking.map((d) => (
                  <div key={d.id} className="border border-amber-200 rounded-lg p-3 bg-amber-50/50">
                    <div className="font-bold text-slate-900" data-testid={`dup-block-name-${d.id}`}>
                      {d.name}
                    </div>
                    <div className="text-xs text-slate-600 mt-1 space-y-0.5">
                      <div>• {t("area_manager")}: <b>{d.area_manager_name}</b></div>
                      {!d.is_admin_branch && !d.is_vacation_branch && (
                        <div>• {t("branch")}: <b>{d.branch_code ? `[${d.branch_code}] ` : ""}{d.branch_name}</b></div>
                      )}
                      {d.supervisor_name && d.supervisor_name !== "—" && (
                        <div>• {t("admin")}: <b>{d.supervisor_name}</b></div>
                      )}
                      <div>• {t("dup_matched_on")}: <b>{d.collided_on.map((c) => t(`dup_field_${c}`)).join(" / ")}</b></div>
                      <div className="text-slate-500">
                        {d.iqama_number && <span className="me-3">{t("iqama_number")}: {d.iqama_number}</span>}
                        {d.employee_id && <span>{t("employee_id")}: {d.employee_id}</span>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="p-4 border-t border-gray-100 flex justify-end gap-2 bg-gray-50 rounded-b-2xl">
              <button className="btn btn-primary" onClick={() => setDupBlocking([])} data-testid="dup-blocking-close">
                <X size={14} /> {t("close")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
